/**
 * Cross-surface URL helper for the ERP (app.anoorehr.com).
 *
 * Mirrors `website/src/lib/urls.ts` so both frontends resolve sibling surfaces
 * to the same hosts. Env-aware: `npm run dev` points cross-surface links at
 * localhost; prod builds point them at the public hosts.
 *
 * Resolution order per URL:
 *   1. Explicit VITE_* env var (overrides everything)
 *   2. Dev default → localhost:<port>
 *   3. Prod default → https://<host>.anoorehr.com
 *
 * The API base URL is not declared here — it stays in `api/client.ts` where
 * axios consumes it directly.
 */
type ViteEnv = {
  readonly DEV?: boolean;
  readonly PROD?: boolean;
  readonly VITE_MARKETING_URL?: string;
  readonly VITE_SUPPORT_URL?: string;
  readonly VITE_APP_URL?: string;
  readonly VITE_CONSOLE_URL?: string;
  readonly VITE_PLATFORM_OWNER?: string;
};

const env = (import.meta as unknown as { env: ViteEnv }).env ?? {};
const isDev = env.DEV === true;

const stripTrailingSlash = (s: string): string => s.replace(/\/+$/, "");

const pick = (
  override: string | undefined,
  devFallback: string,
  prodFallback: string,
): string => stripTrailingSlash(override || (isDev ? devFallback : prodFallback));

export const APP_URL = pick(
  env.VITE_APP_URL,
  "http://localhost:5173",
  "https://app.anoorehr.com",
);
export const CONSOLE_URL = pick(
  env.VITE_CONSOLE_URL,
  "http://localhost:5175",
  "https://console.anoorehr.com",
);
export const MARKETING_URL = pick(
  env.VITE_MARKETING_URL,
  "http://localhost:3000",
  "https://anoorehr.com",
);
export const SUPPORT_URL = pick(
  env.VITE_SUPPORT_URL,
  "http://localhost:3000/support",
  "https://anoorehr.com/support",
);

/**
 * Legal entity that owns/operates the platform — shown in the app footer
 * copyright. This is the platform operator, NOT the tenant company the user is
 * signed into (which would wrongly show e.g. "Vintage Demo Ltd"). Override per
 * white-label deployment via VITE_PLATFORM_OWNER.
 */
export const PLATFORM_OWNER = env.VITE_PLATFORM_OWNER || "Executive Talents Inc";

export const BLOG_URL = `${MARKETING_URL}/blog`;
export const PRICING_URL = `${MARKETING_URL}/pricing`;
export const CONTACT_URL = `${MARKETING_URL}/contact`;
